/**
 * KAN-837: свой профиль менеджера в приложении (/m/profile): ФИО, телефон,
 * на который звонит наш обратный звонок, и когда можно звонить.
 * Профиль есть только у входа с записью менеджера (manager_id из /auth/me);
 * у владельца запрос не уходит. После сохранения обновляются текущий
 * пользователь (имя менеджера) и все запросы менеджеров.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { managersApi } from '@/services/api/managers';
import { MANAGERS_QUERY_KEY } from '@/services/api/queryKeys';
import { FORM_ERROR_KEY, profileErrorsFrom, type ProfileErrors } from '@/lib/managerProfileErrors';
import {
  draftToWorkHours,
  emptyWorkHoursDraft,
  validateWorkHours,
  workHoursToDraft,
  type WorkHoursDraft,
} from '@/lib/workHours';
import type { ManagerSelfBody, ManagerSelfProfile } from '@/types/manager';

export const MY_MANAGER_PROFILE_KEY = [...MANAGERS_QUERY_KEY, 'me'] as const;

const PROFILE_FIELDS = ['last_name', 'first_name', 'middle_name', 'phone', 'work_hours'] as const;
export const FIRST_NAME_REQUIRED = 'Укажите имя';
export const PHONE_REQUIRED = 'Укажите телефон';

export function useMyManagerProfile() {
  const { data: user } = useCurrentUser();
  const hasManager = !!user?.manager_id;
  const query = useQuery({
    queryKey: MY_MANAGER_PROFILE_KEY,
    queryFn: managersApi.getMe,
    enabled: hasManager,
    staleTime: 60_000,
  });
  return { ...query, hasManager };
}

/** Нет телефона: обратный звонок менеджеру не заработает (баннер на вкладках).
 * KAN-862: ответ без поля phone считается профилем без телефона. */
export function useMissingManagerPhone(): boolean {
  const { data, hasManager } = useMyManagerProfile();
  return hasManager && !!data && !(data.phone ?? '').trim();
}

export function useUpdateMyManagerProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: ManagerSelfBody) => managersApi.updateMe(body),
    onSuccess: (profile) => {
      client.setQueryData(MY_MANAGER_PROFILE_KEY, profile);
      void client.invalidateQueries({ queryKey: ['currentUser'] });
      void client.invalidateQueries({ queryKey: MANAGERS_QUERY_KEY });
    },
  });
}

export interface ProfileDraft {
  last_name: string;
  first_name: string;
  middle_name: string;
  phone: string;
  workHours: WorkHoursDraft;
}

type TextField = 'last_name' | 'first_name' | 'middle_name' | 'phone';

function draftFrom(profile: ManagerSelfProfile | undefined): ProfileDraft {
  return {
    last_name: profile?.last_name ?? '',
    first_name: profile?.first_name ?? '',
    middle_name: profile?.middle_name ?? '',
    phone: profile?.phone ?? '',
    workHours: profile ? workHoursToDraft(profile.work_hours) : emptyWorkHoursDraft(),
  };
}

/** Проверка перед отправкой: имя, телефон (без него звонки не работают), часы. */
export function validateProfileDraft(draft: ProfileDraft): ProfileErrors {
  const errors: ProfileErrors = {};
  if (!draft.first_name.trim()) errors.first_name = FIRST_NAME_REQUIRED;
  if (!draft.phone.trim()) errors.phone = PHONE_REQUIRED;
  const hoursError = validateWorkHours(draft.workHours);
  if (hoursError) errors.work_hours = hoursError;
  return errors;
}

function bodyFrom(draft: ProfileDraft): ManagerSelfBody {
  return {
    last_name: draft.last_name.trim(),
    first_name: draft.first_name.trim(),
    middle_name: draft.middle_name.trim(),
    phone: draft.phone.trim(),
    work_hours: draftToWorkHours(draft.workHours),
  };
}

/** Форма экрана профиля: черновик из ответа, проверка, сохранение, ошибки по полям. */
export function useManagerProfileForm() {
  const profile = useMyManagerProfile();
  const update = useUpdateMyManagerProfile();
  const [draft, setDraft] = useState<ProfileDraft>(() => draftFrom(profile.data));
  const [errors, setErrors] = useState<ProfileErrors>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile.data) setDraft(draftFrom(profile.data));
  }, [profile.data]);

  const setField = useCallback((field: TextField, value: string) => {
    setDraft((d) => ({ ...d, [field]: value }));
    setErrors((e) => {
      if (!e[field]) return e;
      const next = { ...e };
      delete next[field];
      return next;
    });
    setSaved(false);
  }, []);

  const setWorkHours = useCallback((workHours: WorkHoursDraft) => {
    setDraft((d) => ({ ...d, workHours }));
    setErrors((e) => {
      if (!e.work_hours) return e;
      const next = { ...e };
      delete next.work_hours;
      return next;
    });
    setSaved(false);
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    const local = validateProfileDraft(draft);
    setErrors(local);
    setSaved(false);
    if (Object.keys(local).length > 0) return false;
    try {
      await update.mutateAsync(bodyFrom(draft));
      setSaved(true);
      return true;
    } catch (err) {
      setErrors(profileErrorsFrom(err, PROFILE_FIELDS));
      return false;
    }
  }, [draft, update]);

  return {
    profile: profile.data,
    isLoading: profile.isLoading,
    isError: profile.isError,
    hasManager: profile.hasManager,
    draft,
    setField,
    setWorkHours,
    save,
    saving: update.isPending,
    saved,
    errors,
    formError: errors[FORM_ERROR_KEY] ?? null,
  };
}
